/**
 * Live Announcements Composable
 *
 * Provides utilities for managing live region announcements to screen readers.
 * Supports different politeness levels and announcement types for optimal
 * accessibility experience.
 */

import { ref, nextTick } from 'vue';

export type AnnouncementPoliteness = 'polite' | 'assertive' | 'off';
export type AnnouncementPriority = 'low' | 'medium' | 'high' | 'critical';

export interface AnnouncementOptions {
  /**
   * Politeness level for ARIA live region
   */
  politeness?: AnnouncementPoliteness;

  /**
   * Priority level - higher priority announcements interrupt lower ones
   */
  priority?: AnnouncementPriority;

  /**
   * Whether the announcement should be atomic
   */
  atomic?: boolean;

  /**
   * Delay before announcement in milliseconds
   */
  delay?: number;

  /**
   * Whether to clear previous announcements of lower priority
   */
  clearPrevious?: boolean;

  /**
   * Category for grouping related announcements
   */
  category?: string;
}

interface QueuedAnnouncement {
  id: string;
  message: string;
  options: Required<AnnouncementOptions>;
  timestamp: number;
}

export const useLiveAnnouncements = () => {
  // Announcement queue for managing priorities
  const announcementQueue = ref<QueuedAnnouncement[]>([]);

  // Internal counter for unique IDs
  let announcementCounter = 0;

  /**
   * Default options for announcements
   */
  const defaultOptions: Required<AnnouncementOptions> = {
    politeness: 'polite',
    priority: 'medium',
    atomic: true,
    delay: 0,
    clearPrevious: false,
    category: 'general',
  };

  /**
   * Get priority score for sorting
   */
  const getPriorityScore = (priority: AnnouncementPriority): number => {
    switch (priority) {
      case 'low':
        return 1;
      case 'medium':
        return 2;
      case 'high':
        return 3;
      case 'critical':
        return 4;
      default:
        return 2;
    }
  };

  /**
   * Clear announcements of lower or equal priority
   */
  const clearLowerPriority = (priority: AnnouncementPriority, category?: string): void => {
    const priorityScore = getPriorityScore(priority);

    announcementQueue.value = announcementQueue.value.filter(announcement => {
      const announcementScore = getPriorityScore(announcement.options.priority);
      const shouldKeep =
        announcementScore > priorityScore ||
        (category && announcement.options.category !== category);
      return shouldKeep;
    });
  };

  /**
   * Update a DOM live region element directly.
   * Logs a warning if the element is missing (e.g. removed by HMR or third-party code).
   */
  const updateLiveRegion = (id: string, value: string): void => {
    const element = document.getElementById(id);
    if (element) {
      element.textContent = value;
    } else if (value) {
      console.warn(
        `[useLiveAnnouncements] Live region element #${id} not found in DOM. Screen reader announcements will not be delivered.`
      );
    }
  };

  /**
   * Process the announcement queue
   */
  const processQueue = async (): Promise<void> => {
    if (announcementQueue.value.length === 0) return;

    // Sort by priority (highest first) then by timestamp (oldest first)
    announcementQueue.value.sort((a, b) => {
      const priorityDiff =
        getPriorityScore(b.options.priority) - getPriorityScore(a.options.priority);
      if (priorityDiff !== 0) return priorityDiff;
      return a.timestamp - b.timestamp;
    });

    // Process the highest priority announcement
    const announcement = announcementQueue.value.shift();
    if (!announcement) return;

    // Apply the announcement based on politeness level
    const targetElementId =
      announcement.options.politeness === 'assertive'
        ? 'assertive-announcements'
        : 'polite-announcements';

    // Wait for next tick to ensure DOM updates
    await nextTick();

    // Clear previous content if requested or if critical priority
    if (announcement.options.clearPrevious || announcement.options.priority === 'critical') {
      updateLiveRegion(targetElementId, '');
      await nextTick();
    }

    // Set the new announcement
    updateLiveRegion(targetElementId, announcement.message);

    // Continue processing queue after a brief delay
    if (announcementQueue.value.length > 0) {
      setTimeout(() => processQueue(), 100);
    }
  };

  /**
   * Make a live announcement
   */
  const announce = (message: string, options: AnnouncementOptions = {}): string => {
    if (!message.trim()) return '';

    const finalOptions: Required<AnnouncementOptions> = {
      ...defaultOptions,
      ...options,
    };

    // Generate unique ID
    const id = `announcement-${++announcementCounter}`;

    // Clear lower priority announcements if requested
    if (finalOptions.clearPrevious) {
      clearLowerPriority(finalOptions.priority, finalOptions.category);
    }

    // Create queued announcement
    const queuedAnnouncement: QueuedAnnouncement = {
      id,
      message,
      options: finalOptions,
      timestamp: Date.now(),
    };

    // Add to queue
    announcementQueue.value.push(queuedAnnouncement);

    // Process queue with optional delay
    if (finalOptions.delay > 0) {
      setTimeout(() => processQueue(), finalOptions.delay);
    } else {
      processQueue();
    }

    return id;
  };

  /**
   * Announce game state changes
   */
  const announceGameState = (state: string, details?: string): string => {
    const message = details ? `${state}. ${details}` : state;
    return announce(message, {
      politeness: 'assertive',
      priority: 'high',
      category: 'game-state',
      clearPrevious: true,
    });
  };

  /**
   * Announce player actions
   */
  const announcePlayerAction = (action: string, player?: string): string => {
    const message = player ? `${player} ${action}` : action;
    return announce(message, {
      politeness: 'polite',
      priority: 'medium',
      category: 'player-action',
    });
  };

  /**
   * Announce turn changes
   */
  const announceTurnChange = (currentPlayer: string, isYourTurn: boolean): string => {
    const message = isYourTurn
      ? "It's your turn to select the letters"
      : `${currentPlayer}'s turn to select the letters`;

    return announce(message, {
      politeness: 'assertive',
      priority: 'high',
      category: 'turn-change',
      clearPrevious: true,
    });
  };

  /**
   * Announce letter selection
   */
  const announceLetterSelection = (letterType: 'vowel' | 'consonant', letter?: string): string => {
    const message = letter ? `${letterType} ${letter} selected` : `${letterType} selected`;

    return announce(message, {
      politeness: 'polite',
      priority: 'medium',
      category: 'letter-selection',
    });
  };

  /**
   * Announce errors or warnings
   */
  const announceError = (error: string): string => {
    return announce(error, {
      politeness: 'assertive',
      priority: 'critical',
      category: 'error',
      clearPrevious: true,
    });
  };

  /**
   * Announce success messages
   */
  const announceSuccess = (message: string): string => {
    return announce(message, {
      politeness: 'assertive',
      priority: 'high',
      category: 'success',
    });
  };

  /**
   * Announce progress updates
   */
  const announceProgress = (message: string): string => {
    return announce(message, {
      politeness: 'polite',
      priority: 'low',
      category: 'progress',
    });
  };

  /**
   * Clear all announcements
   */
  const clearAll = (): void => {
    announcementQueue.value = [];
    updateLiveRegion('polite-announcements', '');
    updateLiveRegion('assertive-announcements', '');
  };

  /**
   * Clear announcements by category
   */
  const clearCategory = (category: string): void => {
    announcementQueue.value = announcementQueue.value.filter(
      announcement => announcement.options.category !== category
    );
  };

  /**
   * Get current queue length
   */
  const getQueueLength = (): number => {
    return announcementQueue.value.length;
  };

  /**
   * Create live region elements for the DOM
   */
  const createLiveRegions = () => {
    // Check if live regions already exist
    if (document.getElementById('polite-announcements')) {
      return;
    }

    // Create polite live region
    const politeRegion = document.createElement('div');
    politeRegion.id = 'polite-announcements';
    politeRegion.setAttribute('aria-live', 'polite');
    politeRegion.setAttribute('aria-atomic', 'true');
    politeRegion.setAttribute('aria-relevant', 'additions text');
    politeRegion.className = 'sr-only';
    politeRegion.textContent = '';
    document.body.appendChild(politeRegion);

    // Create assertive live region
    const assertiveRegion = document.createElement('div');
    assertiveRegion.id = 'assertive-announcements';
    assertiveRegion.setAttribute('aria-live', 'assertive');
    assertiveRegion.setAttribute('aria-atomic', 'true');
    assertiveRegion.setAttribute('aria-relevant', 'additions text');
    assertiveRegion.className = 'sr-only';
    assertiveRegion.textContent = '';
    document.body.appendChild(assertiveRegion);
  };

  return {
    // Core methods
    announce,
    clearAll,
    clearCategory,

    // Specialized announcement methods
    announceGameState,
    announcePlayerAction,
    announceTurnChange,
    announceLetterSelection,
    announceError,
    announceSuccess,
    announceProgress,

    // Utility methods
    getQueueLength,
    createLiveRegions,
  };
};

export default useLiveAnnouncements;
